import { useManager } from 'dreamstate';
import React, { FormEvent, useCallback, useEffect, useState } from 'react';

import {
  rem,
  Flex,
  Box,
  FormControl,
  Header,
  Password,
  FormErrorMessage,
  Button,
} from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { ECredentialsType } from '@vimeo/live-core/api/interaction';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import { useDomInputValue } from '@vimeo/live-core/hooks/react/useDomInputValue';
import {
  IInteractionSessionContext,
  InteractionSessionManager,
} from '@vimeo/live-core/store/interaction/session';
import { createDomName } from '@vimeo/live-core/utils/dom/identifying';
import { ELiveErrorCategory, trackLiveError } from '@vimeo/live-core/utils/tracking';
import { Logger } from '@vimeo/logging';
import { useBroadcastChannel } from '@vimeo/use-broadcast-channel';

import { translations } from '../../../../../translations';
import { PAGE_WRAPPER_STYLE } from '../styles';

interface IInteractionPasswordLayoutProps extends IBaseDomElementProps {
  interactionSessionContext?: IInteractionSessionContext;
}

export function InteractionPasswordLayout({
  id = createDomName('password-screen'),
  className = createDomName('password-screen'),
  interactionSessionContext: {
    sessionId,
    roomUser: { error, isLoading },
    interactionSessionActions: { checkPasswordAuth },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionSessionManager),
}: IInteractionPasswordLayoutProps) {
  const [isSubmitting, setSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [roomPassword, , onRoomPasswordChanged] = useDomInputValue('');

  const isInvalid: boolean = Boolean(error && !isLoading && hasSubmitted);

  const { isChannelActive, sendChannelMessage } = useBroadcastChannel({
    name: interactionToolsConfig.BROADCAST_CHANNEL.NAME,
  });

  const onAuthSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      event.stopPropagation();

      setHasSubmitted(true);

      if (roomPassword) {
        setSubmitting(true);

        await checkPasswordAuth(roomPassword);

        if (isChannelActive) {
          try {
            sendChannelMessage({
              type: interactionToolsConfig.BROADCAST_CHANNEL.MESSAGE_TYPE.NEW_DATA,
              payload: {
                sessionId: String(sessionId),
                type: ECredentialsType.PASSWORD,
                value: roomPassword,
              },
            });
          } catch (error) {
            Logger.getGlobal().error('Failed to send password layout channel message:', error);

            trackLiveError(error, {
              component: 'InteractionPasswordLayout',
              category: ELiveErrorCategory.INTERACTION,
            });
          }
        }
      }
    },
    [roomPassword, checkPasswordAuth, isChannelActive, sessionId, sendChannelMessage],
  );

  useEffect(() => {
    if (error) {
      setSubmitting(false);
    }
  }, [error]);

  return (
    <Flex id={id} className={className} sx={PAGE_WRAPPER_STYLE}>
      <Box margin={'auto'} paddingX={rem(8)}>
        <form
          id={createDomName(id, 'form')}
          className={createDomName(className, 'form')}
          onSubmit={onAuthSubmit}
        >
          <Header size={'lg'} textAlign={'center'} marginBottom={rem(30)} color="text-primary">
            {translations.passwordRequired}
          </Header>

          <Flex>
            <FormControl isInvalid={isInvalid}>
              <Password
                id={createDomName(id, 'form-input')}
                className={createDomName(className, 'form-input')}
                size={'md'}
                placeholder={translations.enterPassword}
                value={roomPassword}
                isDisabled={isLoading}
                onChange={onRoomPasswordChanged}
              />
              <FormErrorMessage>{isInvalid ? translations.incorrectPassword : ''}</FormErrorMessage>
            </FormControl>

            <Button
              id={createDomName(id, 'form-button')}
              className={createDomName(className, 'form-button')}
              type={'submit'}
              isDisabled={!roomPassword || isLoading}
              isLoading={isSubmitting}
              marginLeft={rem(16)}
            >
              {translations.submit}
            </Button>
          </Flex>
        </form>
      </Box>
    </Flex>
  );
}
